import {
  ApartmentOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  SendOutlined,
  StarFilled,
  StopOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Pagination,
  Progress,
  Rate,
  Row,
  Select,
  Spin,
  Switch,
  Tabs,
  Tag,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBotStatus,
  getDesignAgentBotMetrics,
  getDesignAgentDashboardSessions,
  getDesignAgentSessionDetail,
  getRatedMessages,
  humanTakeover,
  setBotStatus,
  submitMessageReview,
  submitSessionReview,
} from "../../apis/design_agent_dashboard.api";
import {
  IAgentChannel,
  IAgentMessage,
  IAgentMode,
  IDesignAgentBotMetrics,
  IDesignAgentDashboardFilters,
  IDesignAgentSessionDetail,
  IDesignAgentSessionSummary,
  IFlowExit,
  IFunnelDetailGroup,
  IFunnelStage,
  IMetricValue,
} from "../../interfaces/design_agent_dashboard.interface";
import { getImageUrl } from "../../helpers/image_helper";
import DesignAgent from "../design_agent";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";
import styles from "./index.module.scss";

const { RangePicker } = DatePicker;

// ----- Helpers -----

const formatRelative = (iso: string) => {
  const d = dayjs(iso);
  const diffMin = dayjs().diff(d, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = dayjs().diff(d, "hour");
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = dayjs().diff(d, "day");
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.format("DD MMM");
};

const MODE_LABEL: Record<IAgentMode, string> = {
  personal: "Personal",
  sales: "Sales",
  admin: "Admin",
};

const modeChipClass = (mode: IAgentMode) => {
  if (mode === "sales") return styles.chipSales;
  if (mode === "admin") return styles.chipAdmin;
  return styles.chipPersonal;
};

const cardModeClass = (mode: IAgentMode) => {
  if (mode === "sales") return styles.cardModeSales;
  if (mode === "admin") return styles.cardModeAdmin;
  return styles.cardModePersonal;
};

const CLASSIFICATION_META: Record<string, { label: string; color: string }> = {
  b2c: { label: "B2C", color: "green" },
  b2b: { label: "B2B", color: "blue" },
  lost: { label: "Lost", color: "red" },
  existing_customer: { label: "Existing", color: "gold" },
  human_transfer: { label: "Human transfer", color: "purple" },
  technical_error: { label: "Technical error", color: "volcano" },
  media_unsupported: { label: "Media unsupported", color: "orange" },
  store_visit: { label: "Store visit", color: "cyan" },
  pending: { label: "Pending", color: "default" },
};

// Tag filter options. Empty default → backend hides 'existing_customer'; pick it to view.
const CLASSIFICATION_FILTER_OPTIONS = [
  { label: "B2C", value: "b2c" },
  { label: "B2B", value: "b2b" },
  { label: "Lost", value: "lost" },
  { label: "Existing", value: "existing_customer" },
  { label: "Human transfer", value: "human_transfer" },
  { label: "Technical error", value: "technical_error" },
  { label: "Media unsupported", value: "media_unsupported" },
  { label: "Store visit", value: "store_visit" },
  { label: "Pending", value: "pending" },
];

const JUDGE_DIMENSION_LABELS: Record<string, string> = {
  resolution: "Resolution",
  helpfulness: "Helpfulness",
  accuracy: "Accuracy",
  qualification_correctness: "Qualification",
  conversion_progress: "Conversion",
  handoff_appropriateness: "Handoff",
  guardrail_adherence: "Guardrails",
  tone: "Tone",
  language_appropriateness: "Language",
};

const PillBadge: React.FC<{ color: string; bg: string; children: React.ReactNode }> = ({
  color,
  bg,
  children,
}) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 999,
      color,
      background: bg,
      lineHeight: 1.7,
    }}
  >
    {children}
  </span>
);

const DrawerKV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={styles.drawerKVRow}>
    <span className={styles.drawerKVLabel}>{label}</span>
    <span className={styles.drawerKVValue}>{value}</span>
  </div>
);

// ============================================================================
// SessionCard
// ============================================================================

const CHANNEL_LABEL: Record<string, string> = { whatsapp: "WhatsApp", web: "Web" };

const SessionCard: React.FC<{
  data: IDesignAgentSessionSummary;
  onClick: () => void;
  onTakeover?: () => void;
}> = ({ data, onClick, onTakeover }) => {
  const { hasPermission } = usePermissions();
  const displayName = data.contact_name || data.user_name;
  const showPhone = data.contact_phone && data.contact_phone !== displayName;

  const [takingOver, setTakingOver] = useState(false);
  const doTakeover = async () => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to take over this chat");
      return;
    }
    if (!data.contact_phone) return;
    setTakingOver(true);
    const res = await humanTakeover(data.contact_phone);
    setTakingOver(false);
    if (res) {
      message.success("Bot stopped — you can take over this chat now.");
      onTakeover?.();
    }
  };
  const canTakeover = data.status === "active" && !!data.contact_phone;

  return (
    <div
      className={`${styles.sessionCard} ${cardModeClass(data.mode)}`}
      onClick={onClick}
    >
      <div className={styles.cardHead}>
        <div className={styles.cardClientBlock}>
          <div className={styles.cardClient}>
            <UserOutlined style={{ color: "#64748b" }} />
            {displayName}
          </div>
          {showPhone && (
            <div className={styles.cardClientPhone}>{data.contact_phone}</div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <div className={styles.cardTime}>{formatRelative(data.created_at)}</div>
          {data.channel && (
            <span className={`${styles.chip} ${data.channel === "whatsapp" ? styles.chipWhatsapp : styles.chipNeutral}`} style={{ fontSize: 10 }}>
              {CHANNEL_LABEL[data.channel] ?? data.channel}
            </span>
          )}
          {canTakeover && (
            <Button
              danger
              size="small"
              loading={takingOver}
              icon={<StopOutlined />}
              onClick={(e) => { e.stopPropagation(); doTakeover(); }}
              style={{ borderRadius: 6, fontSize: 11, height: 24, padding: "0 8px" }}
            >
              Take over
            </Button>
          )}
        </div>
      </div>

      {data.last_message_preview ? (
        <div className={styles.cardMessage}>
          <span className={styles.cardMsgLabel}>Q</span>
          {data.last_message_preview}
        </div>
      ) : (
        <div className={`${styles.cardMessage} ${styles.cardMessageEmpty}`}>(no messages yet)</div>
      )}

      {data.last_agent_response_preview && (
        <div className={`${styles.cardMessage} ${styles.cardMessageAgent}`}>
          <span className={`${styles.cardMsgLabel} ${styles.cardMsgLabelAgent}`}>A</span>
          {data.last_agent_response_preview}
        </div>
      )}

      <div className={styles.cardChips}>
        <span className={`${styles.chip} ${modeChipClass(data.mode)}`}>
          {MODE_LABEL[data.mode]}
        </span>
        {data.classification && data.classification !== "pending" && (
          <PillBadge color="#0f172a" bg="#e2e8f0">
            {CLASSIFICATION_META[data.classification]?.label ?? data.classification}
          </PillBadge>
        )}
        {data.needs_followup && (
          <PillBadge color="#b45309" bg="#fef3c7">follow-up</PillBadge>
        )}
        {data.flagged && (
          <PillBadge color="#b91c1c" bg="#fee2e2">⚠ flagged</PillBadge>
        )}
        {data.eval_overall != null && (
          <PillBadge color="#1d4ed8" bg="#dbeafe">judge {data.eval_overall}/10</PillBadge>
        )}
        <span className={`${styles.chip} ${styles.chipNeutral}`}>
          {data.agent_response_count} replies
        </span>
        {data.tool_use_count > 0 && (
          <span className={`${styles.chip} ${styles.chipNeutral}`}>
            <ToolOutlined /> {data.tool_use_count}
          </span>
        )}
        {data.status === "closed" && (
          <span className={`${styles.chip} ${styles.chipClosed}`}>closed</span>
        )}
        <span className={styles.cardRateCta}>Rate →</span>
      </div>
      {data.avg_rating != null && (
        <div className={styles.cardRating}>
          <StarFilled style={{ color: "#f59e0b", fontSize: 11 }} />
          <span>avg {data.avg_rating}/10</span>
          <span style={{ color: "#94a3b8", fontSize: 10 }}>({data.rated_message_count} msg{data.rated_message_count !== 1 ? "s" : ""})</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MessageRow — single message with inline rating for assistant messages
// ============================================================================

const SEND_TOOLS = ["send_whatsapp_image", "send_whatsapp_video"];

const MessageRow: React.FC<{
  msg: IAgentMessage;
  toolMessages: IAgentMessage[];
  roleBadgeClass: (role: string) => string;
  msgBubbleClass: (role: string) => string;
}> = ({ msg, toolMessages, roleBadgeClass, msgBubbleClass }) => {
  const { hasPermission } = usePermissions();
  const [rating, setRating] = useState(msg.rating ?? 0);
  const [remarks, setRemarks] = useState(msg.review_remarks ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const handleSave = async () => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to submit a review");
      return;
    }
    if (rating === 0) return;
    setSaving(true);
    const res = await submitMessageReview(msg.id, { rating, remarks });
    setSaving(false);
    if (res) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
  };

  const sendCalls = (msg.tool_calls ?? []).filter((tc) => SEND_TOOLS.includes(tc.name));

  // Render product-bearing tool results as cards (these are auto-sent to customer)
  if (msg.role === "tool" && (msg.tool_name === "search_products" || msg.tool_name === "get_inspiration_products")) {
    const products: any[] = msg.tool_result?.products ?? [];
    if (!products.length) return null;
    return (
      <div>
        <div className={styles.msgMeta}>
          <span className={`${styles.msgRoleBadge} ${styles.roleBadgeTool}`}>products</span>
          <span>{dayjs(msg.created_at).format("hh:mm A")}</span>
        </div>
        <div className={styles.sentProductsGroup}>
          <div className={styles.sentProductsHeader}>
            <SendOutlined /> Sent to customer
          </div>
          <div className={styles.sentProductsGrid}>
            {products.map((p: any) => (
              <a key={p.variant_id} className={styles.sentProductCard} href={p.product_url} target="_blank" rel="noopener noreferrer">
                {p.image_url && (
                  <img src={getImageUrl(p.image_url, "200")} className={styles.sentProductImage} alt={p.product_name} />
                )}
                <div className={styles.sentProductCaption}>
                  {p.product_name && <div className={styles.sentProductName}>{p.product_name}</div>}
                  {p.price_per_unit && (
                    <div className={styles.sentProductPrice}>
                      ₹{Number(p.price_per_unit).toLocaleString("en-IN")}/{p.price_unit ?? "unit"}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (msg.role === "tool") return null;
  if (msg.role === "assistant" && !msg.content.trim() && sendCalls.length === 0) return null;

  return (
    <div>
      <div className={styles.msgMeta}>
        <span className={`${styles.msgRoleBadge} ${roleBadgeClass(msg.role)}`}>{msg.role}</span>
        <span>{dayjs(msg.created_at).format("hh:mm A")}</span>
        {msg.role === "assistant" && msg.rating != null && (
          <span className={styles.msgRatingBadge}>
            <StarFilled style={{ color: "#f59e0b", fontSize: 10 }} /> {msg.rating}/10
          </span>
        )}
        {msg.role === "assistant" && (
          <div className={styles.msgActions}>
            {toolMessages.length > 0 && (
              <span
                className={`${styles.msgActionBtn} ${showAnalysis ? styles.msgActionBtnActive : ""}`}
                onClick={() => setShowAnalysis((v) => !v)}
                title="View analysis"
              >
                <BulbOutlined />
              </span>
            )}
            <span className={styles.msgActionBtn} onClick={() => setOpen((v) => !v)}>
              {open ? "▲ hide" : (msg.rating != null ? "✎ edit" : "＋ rate the response")}
            </span>
          </div>
        )}
      </div>

      {msg.role === "assistant" && sendCalls.length > 0 && (
        <div className={styles.sentProductsGroup}>
          <div className={styles.sentProductsHeader}>
            <SendOutlined /> Sent to customer
          </div>
          <div className={styles.sentProductsGrid}>
            {sendCalls.map((tc, i) => (
              <div key={i} className={styles.sentProductCard}>
                {tc.name === "send_whatsapp_image" && tc.arguments.image_url ? (
                  <img src={getImageUrl(tc.arguments.image_url, "200")} className={styles.sentProductImage} alt="product" />
                ) : (
                  <div className={styles.sentProductVideoPlaceholder}>
                    <PlayCircleOutlined /> Video
                  </div>
                )}
                {tc.arguments.caption && (
                  <div className={styles.sentProductCaption}>
                    <div className={styles.sentProductName}>{tc.arguments.caption.split("\n")[0]}</div>
                    {tc.arguments.caption.split("\n")[1] && (
                      <div className={styles.sentProductPrice}>{tc.arguments.caption.split("\n")[1]}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {msg.content.trim() && (
        <div className={`${styles.msgBubble} ${msgBubbleClass(msg.role)}`}>
          {msg.content}
        </div>
      )}

      {msg.role === "assistant" && msg.rating != null && msg.review_remarks && (
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, marginLeft: 4, fontStyle: "italic" }}>
          Remark: {msg.review_remarks}
        </div>
      )}

      {msg.role === "assistant" && showAnalysis && toolMessages.length > 0 && (
        <div className={styles.analysisPanel}>
          <div className={styles.analysisPanelTitle}><BulbOutlined /> Analysis — how this response was built</div>
          {toolMessages.map((tm) => (
            <div key={tm.id} className={styles.analysisBlock}>
              <div className={styles.analysisBlockHeader}>
                <ToolOutlined style={{ color: "#f59e0b" }} />
                <span>{tm.tool_name || "tool"}</span>
              </div>
              {tm.tool_result != null && (
                <div className={styles.analysisBlockBody}>
                  {typeof tm.tool_result === "string" ? tm.tool_result : JSON.stringify(tm.tool_result, null, 2)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {msg.role === "assistant" && open && (
        <div className={styles.msgReviewInline}>
          <div className={styles.msgReviewRatingRow}>
            <span className={styles.reviewStarLabel}>Rating</span>
            <Rate
              count={10}
              value={rating}
              onChange={setRating}
              character={<StarFilled />}
              style={{ fontSize: 14, color: "#f59e0b" }}
            />
            {rating > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{rating}/10</span>}
          </div>
          <Input.TextArea
            rows={2}
            placeholder="Remarks on this response..."
            value={remarks}
            onChange={(e) => { setRemarks(e.target.value); setSaved(false); }}
            style={{ fontSize: 12, borderRadius: 6 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <Button size="small" type="primary" onClick={handleSave} loading={saving} disabled={rating === 0} style={{ borderRadius: 6 }}>
              Save
            </Button>
            {saved && <span style={{ fontSize: 11, color: "#10b981", display: "flex", alignItems: "center", gap: 4 }}><CheckCircleOutlined /> Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DetailDrawer
// ============================================================================

const DetailDrawer: React.FC<{ sessionId: number | null; onClose: () => void }> = ({
  sessionId,
  onClose,
}) => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<IDesignAgentSessionDetail | null>(null);
  useEffect(() => {
    if (!sessionId) { setDetail(null); return; }
    setLoading(true);
    getDesignAgentSessionDetail(sessionId).then((res) => {
      setLoading(false);
      if (res) setDetail(res);
    });
  }, [sessionId]);

  const [sessRating, setSessRating] = useState(0);
  const [sessRemarks, setSessRemarks] = useState("");
  const [sessSaving, setSessSaving] = useState(false);
  const [sessSaved, setSessSaved] = useState(false);

  useEffect(() => {
    setSessRating(detail?.rating ?? 0);
    setSessRemarks(detail?.review_remarks ?? "");
    setSessSaved(false);
  }, [detail]);

  const saveSessionReview = async () => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to submit a review");
      return;
    }
    if (!detail) return;
    setSessSaving(true);
    const res = await submitSessionReview(detail.id, {
      rating: sessRating || undefined,
      remarks: sessRemarks,
    });
    setSessSaving(false);
    if (res) {
      setSessSaved(true);
      setTimeout(() => setSessSaved(false), 3000);
    }
  };

  const [takingOver, setTakingOver] = useState(false);
  const handleTakeover = async () => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to take over this chat");
      return;
    }
    if (!detail?.contact_phone) return;
    setTakingOver(true);
    const res = await humanTakeover(detail.contact_phone);
    setTakingOver(false);
    if (res) {
      message.success("Bot stopped — you can take over this chat now.");
      setDetail((d) => (d ? { ...d, status: "closed" } : d));
    }
  };

  const qual = detail?.room_context?.qualification;
  const evalData = detail?.room_context?.eval;

  const roleBadgeClass = (role: string) =>
    role === "user" ? styles.roleBadgeUser : role === "assistant" ? styles.roleBadgeAssistant : styles.roleBadgeTool;

  const msgBubbleClass = (role: string) =>
    role === "user" ? styles.msgUser : role === "assistant" ? styles.msgAssistant : styles.msgTool;

  return (
    <Drawer
      open={!!sessionId}
      onClose={onClose}
      width={680}
      title={
        detail ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RobotOutlined style={{ color: "#24aab7" }} />
            Session #{detail.id} &mdash; {detail.contact_name || detail.user_name}
            <Tag color={detail.mode === "sales" ? "blue" : detail.mode === "admin" ? "purple" : "cyan"}>
              {MODE_LABEL[detail.mode]}
            </Tag>
          </div>
        ) : "Session Detail"
      }
      destroyOnClose
    >
      {loading && <div style={{ textAlign: "center", padding: 40 }}><Spin size="large" /></div>}

      {!loading && detail && (
        <>
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}><CustomerServiceOutlined /> Session Info</div>
            <div className={styles.drawerCard}>
              {detail.contact_name && (
                <div className={styles.drawerKVRow}>
                  <span className={styles.drawerKVLabel}>Client</span>
                  <span className={styles.drawerKVValue}>
                    {detail.contact_name}{detail.contact_phone && ` (${detail.contact_phone})`}
                  </span>
                </div>
              )}
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Agent / Sales User</span>
                <span className={styles.drawerKVValue}>{detail.user_name}</span>
              </div>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Title</span>
                <span className={styles.drawerKVValue}>{detail.title || "—"}</span>
              </div>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Started</span>
                <span className={styles.drawerKVValue}>
                  {dayjs(detail.created_at).format("DD MMM YYYY, hh:mm A")}
                </span>
              </div>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Status</span>
                <span className={styles.drawerKVValue}>
                  <Tag color={detail.status === "active" ? "green" : "default"}>{detail.status}</Tag>
                </span>
              </div>
              {detail.contact_phone && detail.status === "active" && (
                <div className={styles.drawerKVRow}>
                  <span className={styles.drawerKVLabel}>Bot</span>
                  <span className={styles.drawerKVValue}>
                    <Button
                      danger
                      size="small"
                      loading={takingOver}
                      icon={<StopOutlined />}
                      onClick={handleTakeover}
                      style={{ borderRadius: 6 }}
                    >
                      Stop bot &amp; take over
                    </Button>
                  </span>
                </div>
              )}
            </div>
          </div>

          {qual && Object.keys(qual).length > 0 && (
            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}><BulbOutlined /> Lead Qualification</div>
              <div className={styles.drawerCard}>
                <DrawerKV
                  label="Classification"
                  value={
                    <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Tag color={CLASSIFICATION_META[qual.classification ?? "pending"]?.color}>
                        {CLASSIFICATION_META[qual.classification ?? "pending"]?.label ?? qual.classification}
                      </Tag>
                      {qual.needs_followup && <Tag color="orange">needs follow-up</Tag>}
                      {qual.handoff_done && <Tag>handed off</Tag>}
                      {qual.pitch_delivered && <Tag color="cyan">pitched</Tag>}
                    </span>
                  }
                />
                {qual.client_type && <DrawerKV label="Client type" value={qual.client_type} />}
                {qual.usage_area && <DrawerKV label="Usage area" value={qual.usage_area} />}
                {(qual.delivery_city || qual.delivery_pincode) && (
                  <DrawerKV label="Delivery" value={[qual.delivery_city, qual.delivery_pincode].filter(Boolean).join(" · ")} />
                )}
                {qual.serviceable != null && (
                  <DrawerKV label="Serviceable" value={qual.serviceable ? "Yes" : "No"} />
                )}
                {qual.project_stage && <DrawerKV label="Project stage" value={qual.project_stage} />}
                {qual.quantity_note && <DrawerKV label="Quantity" value={qual.quantity_note} />}
                {qual.lost_reason && <DrawerKV label="Lost reason" value={qual.lost_reason} />}
                {qual.handoff_reason && <DrawerKV label="Handoff reason" value={qual.handoff_reason} />}
              </div>
            </div>
          )}

          {evalData && (
            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>
                <StarFilled style={{ color: "#f59e0b" }} /> Judge Evaluation
                {evalData.flagged && <Tag color="red" style={{ marginLeft: 8 }}>flagged</Tag>}
                {evalData.human_reviewed && <Tag color="green">human reviewed</Tag>}
              </div>
              <div className={styles.drawerCard}>
                {evalData.overall != null && (
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
                    {evalData.overall}
                    <span style={{ fontSize: 13, color: "#94a3b8" }}>/10 overall</span>
                  </div>
                )}
                {evalData.scores && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginBottom: 8 }}>
                    {Object.entries(evalData.scores).map(([dim, score]) => (
                      <div key={dim} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: "#64748b" }}>{JUDGE_DIMENSION_LABELS[dim] ?? dim}</span>
                        <span style={{ fontWeight: 600, color: score != null && score < 6 ? "#b91c1c" : "#334155" }}>
                          {score ?? "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {evalData.coaching_note && (
                  <div style={{ fontSize: 12, color: "#334155", background: "#f8fafc", padding: 8, borderRadius: 6, marginBottom: 6 }}>
                    <b>Coaching:</b> {evalData.coaching_note}
                  </div>
                )}
                {evalData.flag_reason && (
                  <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 6 }}><b>Flag:</b> {evalData.flag_reason}</div>
                )}
                {evalData.unresolved && evalData.unresolved.length > 0 && (
                  <div style={{ fontSize: 12 }}>
                    <div style={{ color: "#64748b", marginBottom: 2 }}>Unresolved questions</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#334155" }}>
                      {evalData.unresolved.map((u, i) => <li key={i}>{u}</li>)}
                    </ul>
                  </div>
                )}
                {evalData.judge_model && (
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>
                    judged by {evalData.judge_model}
                    {evalData.scored_at ? ` · ${dayjs(evalData.scored_at).format("DD MMM, hh:mm A")}` : ""}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}><StarFilled style={{ color: "#f59e0b" }} /> Session Review</div>
            <div className={styles.drawerCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span className={styles.drawerKVLabel}>Rating</span>
                <Rate count={10} value={sessRating} onChange={setSessRating} character={<StarFilled />} style={{ fontSize: 14, color: "#f59e0b" }} />
                {sessRating > 0 && <span style={{ fontSize: 12, fontWeight: 600 }}>{sessRating}/10</span>}
              </div>
              <Input.TextArea
                rows={2}
                placeholder="Overall remarks for this session..."
                value={sessRemarks}
                onChange={(e) => { setSessRemarks(e.target.value); setSessSaved(false); }}
                style={{ fontSize: 12, borderRadius: 6 }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <Button size="small" type="primary" onClick={saveSessionReview} loading={sessSaving} style={{ borderRadius: 6 }}>
                  Save session review
                </Button>
                {sessSaved && (
                  <span style={{ fontSize: 11, color: "#10b981", display: "flex", alignItems: "center", gap: 4 }}>
                    <CheckCircleOutlined /> Saved
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <MessageOutlined /> Conversation ({detail.messages.length} messages)
            </div>
            <div className={styles.thread}>
              {detail.messages.map((msg, idx) => {
                const toolMessages = msg.role === "assistant"
                  ? (() => {
                      const tools: IAgentMessage[] = [];
                      for (const m of detail.messages.slice(idx + 1)) {
                        if (m.role === "tool") tools.push(m);
                        else break;
                      }
                      return tools;
                    })()
                  : [];
                return (
                  <MessageRow
                    key={msg.id}
                    msg={msg}
                    toolMessages={toolMessages}
                    roleBadgeClass={roleBadgeClass}
                    msgBubbleClass={msgBubbleClass}
                  />
                );
              })}
              {detail.messages.length === 0 && <Empty description="No messages" />}
            </div>
          </div>

        </>
      )}
    </Drawer>
  );
};

// ============================================================================
// Sessions Tab Content
// ============================================================================

const SessionsTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [sessions, setSessions] = useState<IDesignAgentSessionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [search, setSearch] = useState("");
  // Dashboard shows personal sessions only — fixed, no mode picker.
  const [mode] = useState<IAgentMode | "">("personal");
  const [channel, setChannel] = useState<IAgentChannel | "">("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [hasReview, setHasReview] = useState<"true" | "false" | "">("");
  const [ratingLt, setRatingLt] = useState<number | null>(null);
  const [classification, setClassification] = useState<string>("");
  const [page, setPage] = useState(1);

  const filters = useMemo<IDesignAgentDashboardFilters>(
    () => ({
      page,
      mode: mode || undefined,
      channel: channel || undefined,
      date_from: dateRange[0]?.startOf("day").toISOString(),
      date_to: dateRange[1]?.endOf("day").toISOString(),
      search: search.trim() || undefined,
      has_review: hasReview || undefined,
      rating_lt: ratingLt ?? undefined,
      classification: classification || undefined,
    }),
    [page, mode, channel, dateRange, search, hasReview, ratingLt, classification]
  );

  const loadSessions = useCallback(() => {
    setSessionsLoading(true);
    getDesignAgentDashboardSessions(filters).then((res) => {
      setSessionsLoading(false);
      if (res) { setSessions(res.results); setTotal(res.count); }
    });
  }, [filters]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const refresh = () => { loadSessions(); };

  const [quickPhone, setQuickPhone] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);
  const quickStopBot = async () => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to control the bot");
      return;
    }
    const phone = quickPhone.trim();
    if (!phone) return;
    if (!/^\d{10}$/.test(phone)) {
      message.error("Enter a valid 10-digit number (no country code or spaces).");
      return;
    }
    setQuickBusy(true);
    const res = await humanTakeover(phone);
    setQuickBusy(false);
    if (res) {
      message.success(`Bot stopped for ${phone} — take over now.`);
      setQuickPhone("");
      loadSessions();
    }
  };

  const [botEnabled, setBotEnabled] = useState<boolean | null>(null);
  const [botBusy, setBotBusy] = useState(false);
  useEffect(() => {
    getBotStatus().then((res) => { if (res) setBotEnabled(res.bot_enabled); });
  }, []);
  const toggleBot = async (next: boolean) => {
    if (!hasPermission("design_agent.dashboard")) {
      message.error("You don't have permission to control the bot");
      return;
    }
    setBotBusy(true);
    const res = await setBotStatus(next);
    setBotBusy(false);
    if (res) {
      setBotEnabled(res.bot_enabled);
      message.success(res.bot_enabled ? "Bot is now LIVE." : "Bot is now OFF.");
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    const data = await getRatedMessages({
      date_from: dateRange[0]?.startOf("day").toISOString(),
      date_to: dateRange[1]?.endOf("day").toISOString(),
      mode: mode || undefined,
      channel: channel || undefined,
    });
    setExportingPdf(false);
    if (!data?.length) return;

    const doc = new jsPDF({ orientation: "landscape" });
    const title = "Rated Responses Export";
    const subtitle = dateRange[0] && dateRange[1]
      ? `${dateRange[0].format("DD MMM YYYY")} – ${dateRange[1].format("DD MMM YYYY")}`
      : `Generated ${dayjs().format("DD MMM YYYY, hh:mm A")}`;
    doc.setFontSize(14);
    doc.text(title, 14, 14);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(subtitle, 14, 21);

    autoTable(doc, {
      startY: 27,
      head: [["Date", "Contact", "User Query", "Bot Response", "Rating", "Remarks"]],
      body: data.map((r) => [
        dayjs(r.created_at).format("DD MMM YY, hh:mm A"),
        `${r.contact_name}\n${r.contact_phone}`,
        r.user_query,
        r.bot_response,
        `${r.rating}/10`,
        r.remarks || "—",
      ]),
      styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [36, 170, 183], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 28 },
        2: { cellWidth: 55 },
        3: { cellWidth: 90 },
        4: { cellWidth: 16 },
        5: { cellWidth: 40 },
      },
    });

    doc.save(`rated-responses-${dayjs().format("YYYY-MM-DD")}.pdf`);
  };

  return (
    <div className={styles.sessionsContent}>
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>
            <RobotOutlined style={{ color: "#24aab7" }} /> Design Agent Dashboard
            {botEnabled !== null && (
              <Tag
                color={botEnabled ? "green" : "red"}
                style={{ marginLeft: 10, borderRadius: 12, fontWeight: 600 }}
              >
                {botEnabled ? "● Bot Live" : "● Bot Off"}
              </Tag>
            )}
            <Switch
              size="small"
              checked={!!botEnabled}
              loading={botBusy || botEnabled === null}
              onChange={toggleBot}
              checkedChildren="On"
              unCheckedChildren="Off"
              style={{ marginLeft: 4 }}
            />
          </h1>
          <p className={styles.pageSubtitle}>
            Audit conversations, review AI responses, and rate session quality
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            placeholder="Phone to stop bot"
            value={quickPhone}
            onChange={(e) => setQuickPhone(e.target.value)}
            onPressEnter={quickStopBot}
            allowClear
            style={{ width: 180, borderRadius: 8 }}
          />
          <Button
            danger
            type="primary"
            icon={<StopOutlined />}
            onClick={quickStopBot}
            loading={quickBusy}
            disabled={!quickPhone.trim()}
            style={{ borderRadius: 8 }}
          >
            Stop bot
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportPdf}
            loading={exportingPdf}
            style={{ borderRadius: 8 }}
          >
            Export PDF
          </Button>
          <Button icon={<ReloadOutlined />} onClick={refresh} style={{ borderRadius: 8 }}>
            Refresh
          </Button>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Search</span>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Client name, phone, or title"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            allowClear
            style={{ borderRadius: 8 }}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Channel</span>
          <Select
            value={channel || undefined}
            onChange={(v) => { setChannel(v ?? ""); setPage(1); }}
            allowClear
            placeholder="All"
            style={{ width: "100%" }}
            options={[
              { label: "WhatsApp", value: "whatsapp" },
              { label: "Web", value: "web" },
              { label: "Unknown", value: "other" },
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Date Range</span>
          <RangePicker
            value={dateRange}
            onChange={(v) => { setDateRange(v ? [v[0], v[1]] : [null, null]); setPage(1); }}
            style={{ borderRadius: 8 }}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Has Rating</span>
          <Select
            value={hasReview || undefined}
            onChange={(v) => { setHasReview(v ?? ""); setPage(1); }}
            allowClear
            placeholder="All sessions"
            style={{ width: "100%" }}
            options={[
              { label: "Rated sessions", value: "true" },
              { label: "Unrated sessions", value: "false" },
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Avg Rating below</span>
          <Select
            value={ratingLt ?? undefined}
            onChange={(v) => { setRatingLt(v ?? null); setPage(1); }}
            allowClear
            placeholder="Any"
            style={{ width: "100%" }}
            options={[
              { label: "Below 4", value: 4 },
              { label: "Below 6", value: 6 },
              { label: "Below 8", value: 8 },
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Tag</span>
          <Select
            value={classification || undefined}
            onChange={(v) => { setClassification(v ?? ""); setPage(1); }}
            allowClear
            placeholder="All tags"
            style={{ width: "100%" }}
            options={CLASSIFICATION_FILTER_OPTIONS}
          />
        </div>
        <Button onClick={() => { setSearch(""); setChannel(""); setDateRange([null, null]); setHasReview(""); setRatingLt(null); setClassification(""); setPage(1); }} style={{ borderRadius: 8 }}>
          Clear
        </Button>
      </div>

      <Spin spinning={sessionsLoading}>
        {sessions.length === 0 && !sessionsLoading ? (
          <div className={styles.centerWrap}>
            <div className={styles.emptyIcon}><MessageOutlined /></div>
            <p className={styles.emptyTitle}>No sessions found</p>
            <p className={styles.emptySubtitle}>Adjust filters or wait for new conversations</p>
          </div>
        ) : (
          <div className={styles.cardsGrid}>
            {sessions.map((s) => (
              <SessionCard key={s.id} data={s} onClick={() => setSelectedId(s.id)} onTakeover={refresh} />
            ))}
          </div>
        )}
      </Spin>

      {total > 20 && (
        <div className={styles.paginationRow}>
          <Pagination current={page} pageSize={20} total={total} onChange={setPage} showSizeChanger={false} />
        </div>
      )}

      <DetailDrawer sessionId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

// ============================================================================
// Metrics Tab
// ============================================================================

const BD_COLORS = ["#24aab7", "#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#0ea5e9", "#64748b"];
const FUNNEL_COLORS = ["#1a8a96", "#24aab7", "#3b82f6", "#8b5cf6", "#10b981"];

const BranchingFunnel: React.FC<{
  stages?: IFunnelStage[];
  activeKey?: string;
  onSelect: (s: IFunnelStage) => void;
}> = ({ stages, activeKey, onSelect }) => {
  if (!stages?.length) return null;

  const renderSpine = (s: IFunnelStage, i: number) => {
    const color = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
    return (
      <div
        className={`${styles.tNode} ${activeKey === s.key ? styles.tNodeActive : ""}`}
        style={{ ["--accent" as string]: color } as React.CSSProperties}
        onClick={() => onSelect(s)}
        role="button"
        tabIndex={0}
      >
        <div className={styles.tNodeHead}>
          <span className={styles.tBadge} style={{ background: color }}>{i + 1}</span>
          <span className={styles.tNodeLabel}>{s.label}</span>
        </div>
        <div className={styles.tNodeHint} title={s.hint}>{s.hint}</div>
        <div className={styles.tNodeCountRow}>
          <span className={styles.tNodeCount}>{s.count.toLocaleString()}</span>
          <span className={styles.tNodeShare}>{s.pct_of_total}% of all</span>
        </div>
        <div className={styles.tNodeMeta}>
          <span className={styles.tRetain}>
            {i === 0 ? "Entry point" : s.pct_of_prev != null ? `${s.pct_of_prev}% from prev` : "—"}
          </span>
          <span className={styles.tNodeLink}>details →</span>
        </div>
      </div>
    );
  };

  const openExit = (ex: IFlowExit) =>
    onSelect({
      key: ex.key,
      label: ex.label,
      hint: "",
      count: ex.count,
      pct_of_total: null,
      pct_of_prev: null,
      details: ex.details ?? [],
      exits: [],
      terminal: true,
      sessions: ex.sessions ?? [],
    });

  const renderLeaf = (ex: IFlowExit) => {
    const clickable = (ex.details?.length ?? 0) > 0;
    return (
      <div
        className={`${styles.tLeaf} ${clickable ? styles.tLeafClickable : ""} ${
          activeKey === ex.key ? styles.tLeafActive : ""
        }`}
        onClick={clickable ? () => openExit(ex) : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        <span className={styles.tLeafLabel} title={ex.label}>{ex.label}</span>
        <span className={styles.tLeafValRow}>
          <span className={styles.tLeafVal}>
            {ex.count.toLocaleString()}
            {ex.pct != null ? <span className={styles.tLeafPct}>{ex.pct}%</span> : null}
          </span>
          {clickable ? <span className={styles.tLeafLink}>details →</span> : null}
        </span>
      </div>
    );
  };

  // ---- compute a balanced grid: every parent is centred over its children ----
  const COL_W = 252;
  const ROW_H = 188;
  const NODE_W = 220;
  const NODE_H = 128;

  type Placed = {
    id: string;
    kind: "spine" | "leaf";
    node?: IFunnelStage;
    exit?: IFlowExit;
    idx: number;
    col: number;
    row: number;
    parentId: string | null;
  };

  const placed: Placed[] = [];
  let spineCol = 0;
  for (let i = 0; i < stages.length; i++) {
    const s = stages[i];
    placed.push({
      id: s.key, kind: "spine", node: s, idx: i, col: spineCol, row: i,
      parentId: i > 0 ? stages[i - 1].key : null,
    });
    const exits = s.exits ?? [];
    const hasNext = !s.terminal && i + 1 < stages.length;
    const n = exits.length + (hasNext ? 1 : 0);
    if (n > 0) {
      const start = spineCol - (n - 1) / 2; // centre children under the parent
      exits.forEach((ex, j) =>
        placed.push({
          id: `${s.key}:${ex.key}`, kind: "leaf", exit: ex, idx: i,
          col: start + j, row: i + 1, parentId: s.key,
        })
      );
      if (hasNext) spineCol = start + exits.length;
    }
  }

  const cols = placed.map((p) => p.col);
  const minCol = Math.min(...cols);
  const maxCol = Math.max(...cols);
  const rows = Math.max(...placed.map((p) => p.row)) + 1;
  const width = (maxCol - minCol + 1) * COL_W;
  const height = (rows - 1) * ROW_H + NODE_H + 70;
  const cx = (col: number) => (col - minCol) * COL_W + COL_W / 2;
  const top = (row: number) => row * ROW_H;

  const spineById: Record<string, Placed> = {};
  placed.forEach((p) => { if (p.kind === "spine") spineById[p.id] = p; });

  const lines = placed
    .filter((p) => p.parentId)
    .map((p) => {
      const par = spineById[p.parentId as string];
      const x1 = cx(par.col);
      const y1 = top(par.row) + NODE_H;
      const x2 = cx(p.col);
      const y2 = top(p.row);
      const my = (y1 + y2) / 2;
      return { id: p.id, d: `M ${x1} ${y1} V ${my} H ${x2} V ${y2}` };
    });

  return (
    <div className={styles.treeScroll}>
      <div className={styles.treeCanvas} style={{ width, height }}>
        <svg className={styles.treeLines} width={width} height={height}>
          {lines.map((l) => (
            <path key={l.id} d={l.d} fill="none" stroke="#cbd5e1" strokeWidth={2} />
          ))}
        </svg>
        {placed.map((p) => (
          <div
            key={p.id}
            className={styles.treeCell}
            style={{ left: cx(p.col) - NODE_W / 2, top: top(p.row), width: NODE_W, height: NODE_H }}
          >
            {p.kind === "spine" && p.node ? renderSpine(p.node, p.idx) : null}
            {p.kind === "leaf" && p.exit ? renderLeaf(p.exit) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const DetailGroup: React.FC<{ group: IFunnelDetailGroup }> = ({ group }) => {
  const max = group.items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  return (
    <div className={styles.drawerGroup}>
      <div className={styles.drawerGroupTitle}>{group.title}</div>
      {group.items.length ? (
        group.items.map((it, idx) => (
          <div className={styles.bdRow} key={it.key}>
            <span className={styles.bdLabel} title={it.label}>{it.label}</span>
            <span className={styles.bdTrack}>
              <span
                className={styles.bdBar}
                style={{
                  width: `${Math.max(2, (it.count / max) * 100)}%`,
                  background: BD_COLORS[idx % BD_COLORS.length],
                }}
              />
            </span>
            <span className={styles.bdVal}>
              {it.count.toLocaleString()}
              {it.pct != null ? <span className={styles.bdValPct}>{it.pct}%</span> : null}
            </span>
          </div>
        ))
      ) : (
        <div className={styles.bdEmpty}>No data in this window</div>
      )}
    </div>
  );
};

// ---- Overview (KPI cards) tab ----

type KpiTone = "primary" | "success" | "warning" | "danger" | "info" | "purple";
const KPI_TONE_HEX: Record<KpiTone, string> = {
  primary: "#24aab7",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#8b5cf6",
};

interface KpiDef {
  key: keyof IDesignAgentBotMetrics;
  label: string;
  hint: string;
  icon: React.ReactNode;
  tone: KpiTone;
  suffix?: string;
  isRate?: boolean;
  numUnit?: string;
  denUnit?: string;
}

const KPI_GROUPS: Array<{ title: string; metrics: KpiDef[] }> = [
  {
    title: "Conversion Funnel",
    metrics: [
      { key: "qualified_conversation_rate", label: "Qualified Conversation Rate", suffix: "%", isRate: true, tone: "success", icon: <CheckCircleOutlined />, hint: "Chats showing a real buying signal" },
      { key: "high_intent_action_rate", label: "High-Intent Action Rate", suffix: "%", isRate: true, tone: "primary", icon: <ThunderboltOutlined />, hint: "Qualified chats that explored products or pricing" },
      { key: "handoff_rate", label: "Handed-to-Human Rate", suffix: "%", isRate: true, tone: "purple", icon: <CustomerServiceOutlined />, hint: "Chats escalated to a human agent" },
      { key: "lead_rate", label: "Lead Capture Rate", suffix: "%", isRate: true, tone: "info", icon: <UserOutlined />, hint: "Chats classified B2C / B2B" },
    ],
  },
  {
    title: "Engagement & Behaviour",
    metrics: [
      { key: "messages_per_session", label: "Messages per Session", tone: "info", icon: <MessageOutlined />, hint: "Avg user + bot messages per chat", numUnit: "messages", denUnit: "chats" },
      { key: "bounce_rate", label: "Bounce Rate", suffix: "%", isRate: true, tone: "warning", icon: <LogoutOutlined />, hint: "Chats with a single user message" },
      { key: "abandonment_rate", label: "Abandonment Rate", suffix: "%", isRate: true, tone: "danger", icon: <ExclamationCircleOutlined />, hint: "Open, un-handed-off chats gone stale (>24h)" },
      { key: "follow_up_rate", label: "Follow-up Rate", suffix: "%", isRate: true, tone: "info", icon: <CustomerServiceOutlined />, hint: "Chats flagged for follow-up" },
    ],
  },
];

const KpiCard: React.FC<{ def: KpiDef; metrics?: IDesignAgentBotMetrics }> = ({ def, metrics }) => {
  const metric = metrics?.[def.key] as IMetricValue | undefined;
  const value = metric?.value;
  const has = value != null;
  return (
    <div className={styles.metricCard}>
      <div className={`${styles.metricIcon} ${styles[`tone_${def.tone}`]}`}>{def.icon}</div>
      <div className={styles.metricBody}>
        <div className={styles.metricLabel}>{def.label}</div>
        <div className={styles.metricValueRow}>
          <span className={styles.metricValue}>{has ? value : "—"}</span>
          {has && def.suffix ? <span className={styles.metricSuffix}>{def.suffix}</span> : null}
        </div>
        {def.isRate && has ? (
          <Progress
            percent={Math.min(100, value as number)}
            showInfo={false}
            size="small"
            strokeColor={KPI_TONE_HEX[def.tone]}
            trailColor="#eef2f6"
          />
        ) : null}
        <div className={styles.metricHint}>{def.hint}</div>
        {metric?.numerator != null && metric?.denominator != null ? (
          <div className={styles.metricFraction}>
            {def.isRate
              ? `${metric.numerator.toLocaleString()} of ${metric.denominator.toLocaleString()}`
              : `${metric.numerator.toLocaleString()} ${def.numUnit ?? ""} · ${metric.denominator.toLocaleString()} ${def.denUnit ?? ""}`}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const OverviewTab: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [metrics, setMetrics] = useState<IDesignAgentBotMetrics | undefined>();
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getDesignAgentBotMetrics({
      channel: "whatsapp",
      date_from: dateRange[0]?.startOf("day").toISOString(),
      date_to: dateRange[1]?.endOf("day").toISOString(),
    }).then((res) => {
      setLoading(false);
      if (res) setMetrics(res);
    });
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  const windowLabel =
    dateRange[0] && dateRange[1]
      ? `${dateRange[0].format("DD MMM")} – ${dateRange[1].format("DD MMM YYYY")}`
      : "Last 30 days";

  return (
    <div className={styles.metricsTab}>
      <div className={styles.metricsToolbar}>
        <div className={styles.metricsToolbarLeft}>
          <span className={styles.metricsTitle}>
            <LineChartOutlined style={{ marginRight: 8 }} />
            WhatsApp Bot Metrics
          </span>
          <span className={styles.metricsWindow}>{windowLabel}</span>
        </div>
        <div className={styles.metricsToolbarRight}>
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange((v as [Dayjs | null, Dayjs | null]) || [null, null])}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className={styles.metricsScroll}>
          <div className={styles.metricsHero}>
            <DatabaseOutlined className={styles.metricsHeroIcon} />
            <div>
              <div className={styles.metricsHeroValue}>{metrics?.total_sessions ?? "—"}</div>
              <div className={styles.metricsHeroLabel}>Total sessions in window</div>
            </div>
          </div>

          {KPI_GROUPS.map((group) => (
            <div className={styles.metricGroup} key={group.title}>
              <div className={styles.metricGroupTitle}>{group.title}</div>
              <Row gutter={[16, 16]}>
                {group.metrics.map((def) => (
                  <Col xs={24} sm={12} lg={8} xxl={6} key={def.key}>
                    <KpiCard def={def} metrics={metrics} />
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

const MetricsTab: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [metrics, setMetrics] = useState<IDesignAgentBotMetrics | undefined>();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IFunnelStage | null>(null);
  const [openSessionId, setOpenSessionId] = useState<number | null>(null);
  const [sessCat, setSessCat] = useState<string>("");
  useEffect(() => { setSessCat(""); }, [selected]);

  const load = useCallback(() => {
    setLoading(true);
    getDesignAgentBotMetrics({
      channel: "whatsapp",
      date_from: dateRange[0]?.startOf("day").toISOString(),
      date_to: dateRange[1]?.endOf("day").toISOString(),
    }).then((res) => {
      setLoading(false);
      if (res) setMetrics(res);
    });
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  const windowLabel =
    dateRange[0] && dateRange[1]
      ? `${dateRange[0].format("DD MMM")} – ${dateRange[1].format("DD MMM YYYY")}`
      : "Last 30 days";

  const msgsPerSession = metrics?.messages_per_session?.value;

  return (
    <div className={styles.metricsTab}>
      <div className={styles.metricsToolbar}>
        <div className={styles.metricsToolbarLeft}>
          <span className={styles.metricsTitle}>
            <LineChartOutlined style={{ marginRight: 8 }} />
            WhatsApp Bot Metrics
          </span>
          <span className={styles.metricsWindow}>{windowLabel}</span>
        </div>
        <div className={styles.metricsToolbarRight}>
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange((v as [Dayjs | null, Dayjs | null]) || [null, null])}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className={styles.metricsScroll}>
          <div className={styles.metricsHero}>
            <DatabaseOutlined className={styles.metricsHeroIcon} />
            <div>
              <div className={styles.metricsHeroValue}>{metrics?.total_sessions ?? "—"}</div>
              <div className={styles.metricsHeroLabel}>Engaged bot conversations in window</div>
            </div>
            {msgsPerSession != null ? (
              <div className={styles.metricsHeroAside}>
                <div className={styles.metricsHeroValue}>{msgsPerSession}</div>
                <div className={styles.metricsHeroLabel}>Avg messages / chat</div>
              </div>
            ) : null}
          </div>

          <div className={styles.metricGroup}>
            <div className={styles.metricGroupTitle}>Conversion Flow</div>
            <div className={styles.panelSub} style={{ marginBottom: 14 }}>
              Each stage splits into chats that continued vs branched off — click a stage for the full breakdown
            </div>
            <BranchingFunnel
              stages={metrics?.flow}
              activeKey={selected?.key}
              onSelect={setSelected}
            />
          </div>
        </div>
      </Spin>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width={460}
        title={
          selected ? (
            <div className={styles.drawerHead}>
              <span className={styles.drawerHeadLabel}>{selected.label}</span>
              <span className={styles.drawerHeadCount}>{selected.count.toLocaleString()} chats</span>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className={styles.drawerSummary}>
              {selected.pct_of_total != null
                ? `${selected.hint} · ${selected.pct_of_total}% of all engaged${
                    selected.pct_of_prev != null ? ` · ${selected.pct_of_prev}% retained from previous` : ""
                  }`
                : `${selected.count.toLocaleString()} chats branched off here — what happened to them:`}
            </div>
            {selected.details?.length ? (
              selected.details.map((g) => <DetailGroup group={g} key={g.title} />)
            ) : (
              <Empty description="No breakdown for this stage" />
            )}
            {selected.sessions?.length
              ? (() => {
                  const cats = Array.from(
                    new Set(selected.sessions!.map((s) => s.category))
                  ).sort();
                  const rows = sessCat
                    ? selected.sessions!.filter((s) => s.category === sessCat)
                    : selected.sessions!;
                  return (
                    <div className={styles.drawerGroup}>
                      <div className={styles.sessHead}>
                        <div className={styles.drawerGroupTitle} style={{ marginBottom: 0 }}>
                          Sessions ({rows.length}
                          {selected.sessions!.length >= 500 ? "+" : ""})
                        </div>
                        <Select
                          size="small"
                          value={sessCat || undefined}
                          placeholder="All categories"
                          allowClear
                          style={{ minWidth: 170 }}
                          onChange={(v) => setSessCat(v || "")}
                          options={cats.map((c) => ({ label: c, value: c }))}
                        />
                      </div>
                      <div className={styles.sessList}>
                        {rows.map((row) => (
                          <div
                            className={styles.sessRow}
                            key={row.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setOpenSessionId(row.id)}
                          >
                            <span className={styles.sessPhone}>{row.phone || "—"}</span>
                            <span className={styles.sessTag}>{row.category}</span>
                            {row.lost_reason ? (
                              <span className={styles.sessReason} title={row.lost_reason}>
                                {row.lost_reason}
                              </span>
                            ) : null}
                            <span className={styles.sessOpen}>view chat →</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              : null}
          </>
        ) : null}
      </Drawer>

      <DetailDrawer sessionId={openSessionId} onClose={() => setOpenSessionId(null)} />
    </div>
  );
};

// ============================================================================
// Main Page
// ============================================================================

const DesignAgentDashboard: React.FC = () => (
  <div className={styles.pageWrapper}>
    <Tabs
      defaultActiveKey="sessions"
      className={styles.mainTabs}
      items={[
        {
          key: "sessions",
          label: <span><DatabaseOutlined style={{ marginRight: 6 }} />Sessions</span>,
          children: <SessionsTab />,
        },
        {
          key: "overview",
          label: <span><LineChartOutlined style={{ marginRight: 6 }} />Overview</span>,
          children: <OverviewTab />,
        },
        {
          key: "metrics",
          label: <span><ApartmentOutlined style={{ marginRight: 6 }} />Funnel</span>,
          children: <MetricsTab />,
        },
        {
          key: "chat",
          label: <span><MessageOutlined style={{ marginRight: 6 }} />Chat</span>,
          children: <DesignAgent />,
        },
      ]}
    />
  </div>
);

export default DesignAgentDashboard;
